'use client'

import { FC, useMemo, useState } from 'react'

import {
  MethodEnum,
  StepEnum,
} from '@/app/forgot-password/components/ForgotPassword/types'
import ModalInformation from '@/app/forgot-password/components/ModalInformation'
import { VariantType } from '@/app/forgot-password/components/ModalInformation/types'
import ResetPasswordForm from '@/app/forgot-password/components/ResetPasswordForm'
import { IApiError } from '@/common/interfaces'
import { ModalBase } from '@/components/commons'
import { IGetContactByDocumentMultiTenant } from '@/services/passwordReset/getContactByDocumentMultiTenantService/interfaces/response'
import {
  IResetPasswordMultiTenantErrorResponse,
  IResetPasswordMultiTenantRequest,
  ResetPasswordMultiTenantService,
} from '@/services/passwordReset/resetPasswordMultiTenantService/resetPasswordMultiTenantService'

import { ILoginAttemptsExceededModalProps } from './types'

const LoginAttemptsExceededModal: FC<ILoginAttemptsExceededModalProps> = ({
  isOpenModal,
  setIsOpenModal,
  document,
  tenantId,
  onGoToLogin,
}) => {
  const [sendButtonLoading, setSendButtonLoading] = useState(false)
  const [isOpenInformationModal, setIsOpenInformationModal] = useState(false)
  const [modalVariant, setModalVariant] = useState<VariantType>('customMessage')
  const [customMessageError, setCustomMessageError] = useState<
    Array<IApiError> | string
  >([] as Array<IApiError>)
  const [step, setStep] = useState<StepEnum>(StepEnum.CONFIRM_RESET_WITH_CODE)

  const contacts = useMemo<IGetContactByDocumentMultiTenant>(
    () => ({
      notification: '',
      listForResetByTenant: [
        {
          document,
          email: '',
          cellphone: '',
          tenantId,
          tenantName: '',
        },
      ],
    }),
    [document, tenantId],
  )

  async function handleResetPassword(params: IResetPasswordMultiTenantRequest) {
    setSendButtonLoading(true)

    await ResetPasswordMultiTenantService(params)
      .then((response) => {
        if (response.status === 200) {
          setModalVariant('resetPasswordSuccess')
          setIsOpenInformationModal(true)
        }
      })
      .catch((error: IResetPasswordMultiTenantErrorResponse) => {
        setCustomMessageError(
          error?.response?.data ?? 'Erro ao tentar alterar a senha.',
        )
        setModalVariant('customMessage')
        setIsOpenInformationModal(true)
      })
      .finally(() => setSendButtonLoading(false))
  }

  function handleCloseModal() {
    setIsOpenInformationModal(false)
    setIsOpenModal(false)
  }

  function handleGoToLogin() {
    handleCloseModal()
    onGoToLogin?.()
  }

  return (
    <ModalBase
      className="w-full max-w-[437px]"
      isOpenModal={isOpenModal}
      setIsOpenModal={(value) => setIsOpenModal(value as boolean)}
      positionCenter
    >
      <ModalBase.Header>
        <h6 className="prose-headline-h6 text-primary-900">Alterar senha!</h6>
        <p className="prose-body text-primary-900 mt-2">
          Você excedeu o número de tentativas de login.{' '}
          <strong>Enviamos um e-mail</strong> com um código de verificação. Por
          favor, utilize esse código para redefinir sua senha.
        </p>
      </ModalBase.Header>

      <ModalBase.Body>
        <ResetPasswordForm
          key={`login-attempts-exceeded-${isOpenModal}`}
          step={step}
          setStep={setStep}
          handleResetPassword={handleResetPassword}
          sendButtonLoading={sendButtonLoading}
          handleSendCodeReset={async () => undefined}
          contacts={contacts}
          method={MethodEnum.EMAIL}
          selectedTenantId={tenantId}
          callToAction={handleCloseModal}
        />
      </ModalBase.Body>

      {isOpenInformationModal ? (
        <ModalInformation
          variant={modalVariant}
          isOpenModal={isOpenInformationModal}
          setIsOpenModal={setIsOpenInformationModal}
          customMessage={customMessageError}
          handleGoToLogin={handleGoToLogin}
        />
      ) : null}
    </ModalBase>
  )
}

export default LoginAttemptsExceededModal
